/**
 * Central error telemetry — one place every ErrorBoundary calls.
 *
 * Today this routes to `console.error` so dev sees full stacks in the
 * terminal. When Sentry is installed (`@sentry/nextjs`), swap the body of
 * `_send` for `Sentry.captureException(error, { extra })`. Nothing else
 * has to change — every boundary already calls `reportError(...)` with
 * the right shape.
 */

import type { ErrorInfo } from 'react';

import { errorCode } from './error-code';

export interface ErrorContext {
  /** Short slug for the boundary scope (`pm_render`, `dash_load`, etc.). */
  scope: string;
  /** Optional user id from Redux, when available. */
  userId?: string;
  /** Current route path. */
  pathname?: string;
  /** Anything else worth attaching. */
  extra?: Record<string, unknown>;
}

export interface ReportedError {
  code: string;
  message: string;
  occurredAt: string;
  scope: string;
}

/**
 * Submit an error to the telemetry pipeline. Returns the minimal record the
 * UI needs to render the user-facing fallback — `code` is the copyable
 * support identifier, `occurredAt` the ISO timestamp.
 */
export function reportError(
  error: unknown,
  info: ErrorInfo | null,
  context: ErrorContext,
): ReportedError {
  const code = errorCode(context.scope, error);
  const occurredAt = new Date().toISOString();
  const message = error instanceof Error ? error.message : String(error);

  _send(error, info, { code, occurredAt, ...context });

  return { code, message, occurredAt, scope: context.scope };
}

function _send(
  error: unknown,
  info: ErrorInfo | null,
  meta: { code: string; occurredAt: string } & ErrorContext,
): void {
  // Sentry hook: when `@sentry/nextjs` lands, replace this body with
  // `Sentry.captureException(error, { tags: { code, scope }, extra: { info, ... } })`
  // and drop the console.error fallback below.
  // Until then: log everything to the browser console so dev can see it.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[propmind.error]', {
      code: meta.code,
      scope: meta.scope,
      occurredAt: meta.occurredAt,
      pathname: meta.pathname,
      userId: meta.userId,
      error,
      componentStack: info?.componentStack,
      extra: meta.extra,
    });
  }
}
